import { faUpload, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Tooltip } from "@mui/material";
import { useCallback, useState } from "react";

import { StoppageDto } from "../../../DTOs/Stoppage/StoppageDto";
import { stoppagesAjax } from "../../../ajax/StoppagesAjax";
import { useSnackbar } from "notistack";
import { useOnlineStatus } from "../../../components/Hooks/useOnlineStatus";
import { useCanWrite, useIsReadOnly } from "../../../utilities/helpers/roleUtilities";

type UploadStoppageImageButtonProps = {
  stoppage: StoppageDto;
  onStoppageUpdated?: (updated: StoppageDto) => void;
};

const UploadStoppageImageButton: React.FC<UploadStoppageImageButtonProps> = ({
  stoppage,
  onStoppageUpdated
}) => {
  const isReadOnly = useIsReadOnly();
  const canUpload = useCanWrite("Stoppages.UploadImage");
  const { enqueueSnackbar } = useSnackbar();
  const [isWorking, setIsWorking] = useState(false);
  const isOnline = useOnlineStatus();

  const handleFileUpload = useCallback(async (stoppage: StoppageDto, file?: File) => {
    if (!file || !stoppage.stoppageId) {
      enqueueSnackbar('Invalid stoppage selected, please try again', { variant: 'error', autoHideDuration: 3000 });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await stoppagesAjax.uploadStoppageImage(stoppage.stoppageId, formData);

      if (!response.success || !response.data) {
        enqueueSnackbar('Error uploading image, please try again', { variant: 'error', autoHideDuration: 3000 });
        return;
      }

      onStoppageUpdated?.(response.data);
      enqueueSnackbar('Image uploaded successfully', { variant: 'success', autoHideDuration: 3000 });
    } catch (error) {
      enqueueSnackbar('Error uploading image, please try again', { variant: 'error', autoHideDuration: 3000 });
    }
  }, [enqueueSnackbar, onStoppageUpdated]);

  if (!canUpload) return null;

  return (
    <Tooltip title="Upload Image" arrow placement="top">
      <Button
        variant="outlined"
        component="label"
        classes={{ root: '!min-w-0 flex flex-1 items-center gap-4 relative justify-center !bg-theme-surface-2 hover:!bg-theme-surface' }}
        size="small"
        sx={{ padding: 0.8 }}
        disabled={isWorking || isReadOnly || !isOnline}
      >
        <span className="absolute flex items-center justify-center text-theme-text">
          <FontAwesomeIcon
            icon={faUpload}
            className={`text-lg transition-opacity ease-in-out ${isWorking ? 'opacity-0' : 'opacity-100'}`}
          />
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            className={`text-lg absolute left-0 transition-opacity ease-in-out ${isWorking ? 'opacity-100' : 'opacity-0'}`}
          />
        </span>
        <input
          type="file"
          hidden
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (file) {
              setIsWorking(true);
              await handleFileUpload(stoppage, file);
              setIsWorking(false);
            }
          }}
        />
      </Button>
    </Tooltip>
  );
};

export default UploadStoppageImageButton;